/**
 * PostHog feature-flag readiness atom + helpers.
 *
 * Kept out of `posthog-provider.tsx` so that file only exports the provider
 * component (react-refresh/only-export-components ratchet).
 */
import { resetPosthogWorkspaceFlagsSynced } from '@/providers/posthog-workspace-flags';
import { isSaasTelemetryEnabled } from '@/utils/buildProfile';
import { atom, getDefaultStore, useAtomValue } from 'jotai';
import posthog from 'posthog-js';

// TELEMETRY-GATE (design D6): when telemetry is off, PostHog is never
// initialized, so nothing must ever await its readiness — flags are "ready"
// immediately and callers fall back to their code defaults.
export const posthogFlagsReadyAtom = atom(!isSaasTelemetryEnabled);

const jotaiStore = getDefaultStore();

/**
 * Reset PostHog identity and re-fetch feature flags.
 * Call this during logout instead of calling posthog.reset() directly.
 */
export function invalidatePosthogFlags(): void {
  // TELEMETRY-GATE: nothing to reset or re-fetch when PostHog never initialized.
  if (!isSaasTelemetryEnabled) return;
  posthog.reset();
  jotaiStore.set(posthogFlagsReadyAtom, false);
  resetPosthogWorkspaceFlagsSynced();
}

/**
 * Returns whether PostHog feature flags have loaded.
 * Use in pages that depend on feature flags for routing or conditional rendering.
 */
export function usePosthogFlagsReady(): boolean {
  return useAtomValue(posthogFlagsReadyAtom);
}
